import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  Breadcrumbs,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { format } from "date-fns";
import { MatchAssignmentPanel } from "../components/matchdays";
import { get, post, delWithBody } from "../utils/networkUtils";

const MatchdayBuilderPage = () => {
  const { id: tournamentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlMatchdayId = searchParams.get("matchday");

  const [tournament, setTournament] = useState(null);
  const [matchdays, setMatchdays] = useState([]);
  const [selectedMatchdayId, setSelectedMatchdayId] = useState(
    urlMatchdayId || ""
  );
  const [selectedMatchday, setSelectedMatchday] = useState(null);
  const [assignedMatches, setAssignedMatches] = useState([]);
  const [availableMatches, setAvailableMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Filters
  const [dateFilter, setDateFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Fetch all matchdays for the tournament
  const fetchMatchdays = useCallback(async () => {
    try {
      const response = await get(
        `/admin/matchday?tournamentCalendarId=${tournamentId}&limit=100`
      );

      if (response?.data?.matchdays) {
        const mappedMatchdays = response.data.matchdays.map((md) => ({
          id: md.id,
          matchdayNumber: md.matchdayNumber,
          name: md.name,
          expectedMatchCount: md.expectedMatchCount,
          assignedMatches: md.assignedMatches || 0,
          status: md.status,
          startsAt: md.startsAt,
          endsAt: md.endsAt,
          locksAt: md.locksAt,
        }));
        setMatchdays(mappedMatchdays);
        return mappedMatchdays;
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch matchdays:", error);
      setSnackbar({
        open: true,
        message: "Failed to load matchdays",
        severity: "error",
      });
      return [];
    }
  }, [tournamentId]);

  // Fetch available matches (not assigned to any matchday)
  const fetchAvailableMatches = useCallback(async (matchdayId) => {
    if (!matchdayId) return;

    try {
      const response = await get(
        `/admin/matchday/${matchdayId}/available-matches`
      );

      if (response?.data?.matches) {
        const mapped = response.data.matches.map((m) => ({
          id: m.id,
          optaFixtureId: m.optaFixtureId,
          kickoffAt: m.kickoffAt,
          status: m.status,
          homeScore: m.homeScore,
          awayScore: m.awayScore,
          homeContestantId: m.homeContestantId,
          awayContestantId: m.awayContestantId,
          homeContestantName: m.homeContestantName || "TBD",
          awayContestantName: m.awayContestantName || "TBD",
        }));

        setAvailableMatches(mapped);
      } else {
        setAvailableMatches([]);
      }
    } catch (error) {
      console.error("Failed to fetch available matches:", error);
      setAvailableMatches([]);
    }
  }, []);

  // Fetch assigned matches for selected matchday
  const fetchAssignedMatches = useCallback(async (matchdayId) => {
    if (!matchdayId) return;

    try {
      const response = await get(`/admin/matchday/${matchdayId}/matches`);

      if (response?.data?.matches) {
        const mapped = response.data.matches.map((m) => ({
          id: m.id,
          optaFixtureId: m.optaFixtureId,
          kickoffAt: m.kickoffAt,
          status: m.status,
          homeScore: m.homeScore,
          awayScore: m.awayScore,
          homeContestantId: m.homeContestantId,
          awayContestantId: m.awayContestantId,
          homeContestantName: m.homeContestantName || "TBD",
          awayContestantName: m.awayContestantName || "TBD",
        }));

        setAssignedMatches(mapped);
      } else {
        setAssignedMatches([]);
      }
    } catch (error) {
      console.error("Failed to fetch assigned matches:", error);
      setAssignedMatches([]);
    }
  }, []);

  const fetchTournament = useCallback(async () => {
    try {
      const response = await get(`/admin/tournament-calendar/${tournamentId}`);
      if (response?.data) {
        setTournament(response.data);
      } else {
        setTournament(null);
      }
    } catch (error) {
      console.error("Failed to fetch tournament:", error);
      setTournament(null);
    }
  }, [tournamentId]);

  const isWorldCup =
    tournament?.competition_name?.toLowerCase().includes("world cup") ||
    tournament?.competition_name?.toLowerCase().includes("fifa world");

  const loadData = useCallback(async () => {
    setLoading(true);

    await fetchTournament();

    // Fetch matchdays
    const allMatchdays = await fetchMatchdays();

    // If matchday is specified in URL, load its data
    const targetMatchdayId = urlMatchdayId || selectedMatchdayId;
    if (targetMatchdayId) {
      const md = allMatchdays.find((m) => m.id === targetMatchdayId);
      if (md) {
        setSelectedMatchday(md);
        setSelectedMatchdayId(md.id);
        await Promise.all([
          fetchAssignedMatches(md.id),
          fetchAvailableMatches(md.id),
        ]);
      }
    }

    setLoading(false);
  }, [
    fetchTournament,
    fetchMatchdays,
    fetchAvailableMatches,
    fetchAssignedMatches,
    urlMatchdayId,
    selectedMatchdayId,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Keep selectedMatchday in sync with matchdays list so dates/counts stay
  // up-to-date after assign/remove operations refetch the list.
  useEffect(() => {
    if (selectedMatchdayId && matchdays.length > 0) {
      const md = matchdays.find((m) => m.id === selectedMatchdayId);
      if (md) {
        setSelectedMatchday(md);
      }
    }
  }, [matchdays, selectedMatchdayId]);

  // Update URL when matchday selection changes
  const handleMatchdayChange = async (event) => {
    const newMatchdayId = event.target.value;
    setSelectedMatchdayId(newMatchdayId);

    if (newMatchdayId) {
      const md = matchdays.find((m) => m.id === newMatchdayId);
      setSelectedMatchday(md);
      await Promise.all([
        fetchAssignedMatches(newMatchdayId),
        fetchAvailableMatches(newMatchdayId),
      ]);

      // Update URL without reloading
      setSearchParams({ matchday: newMatchdayId });
    } else {
      setSelectedMatchday(null);
      setAssignedMatches([]);
      setAvailableMatches([]);
      setSearchParams({});
    }
  };

  // Filter available matches
  const filteredAvailableMatches = useMemo(() => {
    return availableMatches.filter((match) => {
      if (dateFilter && match.kickoffAt) {
        const matchDate = new Date(match.kickoffAt).toISOString().split("T")[0];
        if (matchDate !== dateFilter) return false;
      }

      if (teamFilter) {
        const teamLower = teamFilter.toLowerCase();
        if (
          !match.homeContestantName.toLowerCase().includes(teamLower) &&
          !match.awayContestantName.toLowerCase().includes(teamLower)
        ) {
          return false;
        }
      }

      if (statusFilter && match.status !== statusFilter) {
        return false;
      }

      return true;
    });
  }, [availableMatches, dateFilter, teamFilter, statusFilter]);

  const handleAssignMatches = async (matchIds) => {
    if (!selectedMatchdayId) {
      setSnackbar({
        open: true,
        message: "Please select a matchday first",
        severity: "warning",
      });
      return;
    }

    try {
      setSaving(true);

      // Use bulk assign API
      const response = await post(
        `/admin/matchday/${selectedMatchdayId}/matches/bulk`,
        {
          matchIds: matchIds,
        }
      );

      if (response?.success) {
        // Move matches from available to assigned in state
        const matchesToAssign = availableMatches.filter((m) =>
          matchIds.includes(m.id)
        );
        setAssignedMatches((prev) => [...prev, ...matchesToAssign]);
        setAvailableMatches((prev) =>
          prev.filter((m) => !matchIds.includes(m.id))
        );

        // Refresh matchday list so dates (startsAt/endsAt/locksAt) are in sync
        await fetchMatchdays();

        setSnackbar({
          open: true,
          message: `${matchIds.length} match(es) assigned successfully`,
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Failed to assign matches:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to assign matches",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMatches = async (matchIds) => {
    try {
      setSaving(true);

      // Use bulk remove API
      const response = await delWithBody(
        `/admin/matchday/${selectedMatchdayId}/matches/bulk/remove`,
        {
          matchIds: matchIds,
        }
      );

      if (response?.success) {
        // Move matches from assigned to available in state
        const matchesToRemove = assignedMatches.filter((m) =>
          matchIds.includes(m.id)
        );
        setAssignedMatches((prev) =>
          prev.filter((m) => !matchIds.includes(m.id))
        );
        setAvailableMatches((prev) => [...prev, ...matchesToRemove]);

        // Refresh matchday list so dates (startsAt/endsAt/locksAt) are in sync
        await fetchMatchdays();

        setSnackbar({
          open: true,
          message: `${matchIds.length} match(es) removed successfully`,
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Failed to remove matches:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to remove matches",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    // All changes are saved immediately in this implementation
    // The save button is mostly for user confirmation
    setSnackbar({
      open: true,
      message: `All changes saved for ${selectedMatchday?.name}!`,
      severity: "success",
    });
  };

  const handleBack = () => {
    navigate(`/dashboard/tournaments/${tournamentId}/matchdays`);
  };

  const clearFilters = () => {
    setDateFilter("");
    setTeamFilter("");
    setStatusFilter("");
  };

  const isFull =
    selectedMatchday?.expectedMatchCount &&
    assignedMatches.length >= selectedMatchday.expectedMatchCount;
  const isOverCapacity =
    selectedMatchday?.expectedMatchCount &&
    assignedMatches.length > selectedMatchday.expectedMatchCount;

  // Get unique teams for filter
  const allTeams = useMemo(() => {
    const teams = new Set();
    availableMatches.forEach((m) => {
      teams.add(m.homeContestantName);
      teams.add(m.awayContestantName);
    });
    return Array.from(teams).sort();
  }, [availableMatches]);

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 4 }}>
          Tournament not found
        </Alert>
      </Container>
    );
  }

  if (!isWorldCup) {
    return (
      <Container maxWidth="xl">
        <Alert severity="warning" sx={{ mt: 4 }}>
          Matchday builder is only available for World Cup tournaments.
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/dashboard/tournaments/${tournamentId}`)}
          sx={{ mt: 2 }}
        >
          Back to Tournament
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Matchday Builder | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Typography
            component={RouterLink}
            to="/dashboard/tournaments"
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Tournaments
          </Typography>
          <Typography
            component={RouterLink}
            to={`/dashboard/tournaments/${tournamentId}`}
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {tournament?.competition_name || "Tournament"}
          </Typography>
          <Typography
            component={RouterLink}
            to={`/dashboard/tournaments/${tournamentId}/matchdays`}
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Matchdays
          </Typography>
          <Typography color="text.primary">Matchday Builder</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4">Matchday Builder</Typography>
            {selectedMatchday && (
              <>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {selectedMatchday.name} • {assignedMatches.length} /{" "}
                  {selectedMatchday.expectedMatchCount} matches
                </Typography>
                {selectedMatchday.startsAt && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {format(new Date(selectedMatchday.startsAt), "MMM dd, HH:mm")}
                    {" — "}
                    {format(new Date(selectedMatchday.endsAt), "MMM dd, HH:mm")}
                    {" • Lock: "}
                    {format(new Date(selectedMatchday.locksAt), "MMM dd, HH:mm")}
                  </Typography>
                )}
              </>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || !selectedMatchdayId}
            >
              {saving ? <CircularProgress size={20} /> : "Save Changes"}
            </Button>
          </Box>
        </Box>

        {/* Matchday Selector */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Select Matchday</InputLabel>
            <Select
              value={selectedMatchdayId}
              onChange={handleMatchdayChange}
              label="Select Matchday"
            >
              <MenuItem value="">
                <em>-- Select a matchday to assign matches --</em>
              </MenuItem>
              {matchdays.map((md) => (
                <MenuItem key={md.id} value={md.id}>
                  Matchday {md.matchdayNumber}: {md.name}
                  <Typography
                    component="span"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    ({md.assignedMatches}/{md.expectedMatchCount} matches)
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!selectedMatchdayId && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Please select a matchday from the dropdown above to start
              assigning matches.
            </Alert>
          )}

          {isOverCapacity && (
            <Alert severity="error" sx={{ mt: 2 }}>
              This matchday has{" "}
              {assignedMatches.length - selectedMatchday.expectedMatchCount}{" "}
              extra match(es).
            </Alert>
          )}
          {isFull && !isOverCapacity && (
            <Alert severity="success" sx={{ mt: 2 }}>
              This matchday is at full capacity.
            </Alert>
          )}
        </Paper>

        {/* Filters Panel */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <FilterIcon color="action" />
            <Typography variant="h6">Filters</Typography>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Team"
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
                size="small"
              >
                <MenuItem value="">All Teams</MenuItem>
                {allTeams.map((team) => (
                  <MenuItem key={team} value={team}>
                    {team}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                size="small"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="fixture">Upcoming</MenuItem>
                <MenuItem value="playing">Live</MenuItem>
                <MenuItem value="played">Played</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                onClick={clearFilters}
                fullWidth
                disabled={!dateFilter && !teamFilter && !statusFilter}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>

          {/* Active Filters */}
          {(dateFilter || teamFilter || statusFilter) && (
            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Active filters:
              </Typography>
              {dateFilter && (
                <Chip
                  size="small"
                  label={`Date: ${dateFilter}`}
                  onDelete={() => setDateFilter("")}
                />
              )}
              {teamFilter && (
                <Chip
                  size="small"
                  label={`Team: ${teamFilter}`}
                  onDelete={() => setTeamFilter("")}
                />
              )}
              {statusFilter && (
                <Chip
                  size="small"
                  label={`Status: ${statusFilter}`}
                  onDelete={() => setStatusFilter("")}
                />
              )}
            </Box>
          )}
        </Paper>

        {/* Main Assignment Panel */}
        <Paper sx={{ p: 3 }}>
          {selectedMatchdayId ? (
            <MatchAssignmentPanel
              availableMatches={filteredAvailableMatches}
              assignedMatches={assignedMatches}
              expectedMatchCount={selectedMatchday?.expectedMatchCount}
              onAssignMatches={handleAssignMatches}
              onRemoveMatches={handleRemoveMatches}
              isCompact={false}
              loading={saving}
            />
          ) : (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Matchday Selected
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please select a matchday from the dropdown above to view and
                assign matches.
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default MatchdayBuilderPage;
